import React from "react";
import {
  FormControl,
  InputLabel,
  Grid,
  Radio,
  Box,
  TextField,
  Typography,
  MenuItem,
  RadioGroup,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Select,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import {
  DateTimeValidationError,
  DateValidationError,
  PickerChangeHandlerContext,
  TimeValidationError,
} from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { MembersFieldDto } from "../../types/membersTypes";
import NumberField from "./NumberField";

const sanitizeFieldIdentifier = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

type Field = {
  label: string;
  state: unknown | unknown[];
  width?: "half" | "full";
} & (
  | {
      type: "RadioButton" | "Dropdown";
      onChangeCallback: ((event: React.ChangeEvent<HTMLInputElement>) => void) | null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values: Array<{ label: string; value: any }>;
      disabled?: boolean;
      helperText?: string;
      error?: boolean;
    }
  | {
      type: "TextBig";
      onChangeCallback: ((event: React.ChangeEvent<HTMLInputElement>) => void) | null;
      rows: number;
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
    }
  | {
      type: "Date";
      onChangeCallback:
        | ((value: Dayjs | null, context: PickerChangeHandlerContext<DateValidationError>) => void)
        | null;
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
      views?: ("year" | "month" | "day")[];
      format?: string;
    }
  | {
      type: "Text";
      onChangeCallback: ((event: React.ChangeEvent<HTMLInputElement>) => void) | null;
      inputType?: "number";
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
    }
  | {
      type: "Checkbox";
      onChangeCallback: ((event: React.ChangeEvent<HTMLInputElement>) => void) | null;
      disabled?: boolean;
    }
  | {
      type: "Time";
      onChangeCallback:
        | ((value: Dayjs | null, context: PickerChangeHandlerContext<TimeValidationError>) => void)
        | null;
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
    }
  | {
      type: "DateTime";
      onChangeCallback:
        | ((value: Dayjs | null, context: PickerChangeHandlerContext<DateTimeValidationError>) => void)
        | null;
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
    }
  | {
      type: "Autocomplete";
      onChangeCallback: (event: React.ChangeEvent<object>, value: unknown[] | unknown) => void | null;
      value: unknown[] | unknown;
      options: unknown[];
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
      multiple?: boolean;
    }
  | {
      type: "MembersAutocomplete";
      onChangeCallback: (event: React.ChangeEvent<object>, value: MembersFieldDto[]) => void | null;
      value: MembersFieldDto[];
      options: MembersFieldDto[];
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
      multiple?: boolean;
    }
  | {
      type: "MemberAutocomplete";
      onChangeCallback: (event: React.SyntheticEvent, value: MembersFieldDto | null) => void | null;
      value: MembersFieldDto | null;
      options: MembersFieldDto[];
      error?: boolean;
      helperText?: string;
      disabled?: boolean;
    }
);
interface Props {
  title?: string;
  fields: Array<Field>;
}

const FieldSection = (props: Props) => {
  const theme = useTheme();

  const styles = {
    fieldSectionBox: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      flexWrap: "wrap",
      paddingBottom: theme.spacing(2),
    },
    fieldSectionTitle: {
      width: "100%",
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
    fieldItem: {
      flexGrow: 1,
      marginTop: theme.spacing(1),
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    radioButtonField: {
      display: "flex",
      flexDirection: "row",
      flexGrow: 1,
    },
    gridItem: {
      display: "flex",
    },
    radioButtonItem: {
      flexGrow: 1,
    },
    checkboxField: {
      paddingLeft: 1,
    },
  };

  const renderFields = (fields: Field[]) => {
    return fields.map((field: Field, index: number) => {
      // Used for form-control id/name/labelId linkage across generated field types.
      const fieldId = `field-${index}-${sanitizeFieldIdentifier(field.label) || "input"}`;
      let fieldElement: React.JSX.Element = <div></div>;
      if (field.type === "Dropdown") {
        fieldElement = (
          <FormControl
            sx={{
              flexGrow: 1,
              marginTop: theme.spacing(1),
              marginLeft: theme.spacing(1),
              marginRight: theme.spacing(1),
            }}
            key={index}
            disabled={field.disabled}
            error={field.error}
          >
            <InputLabel id={`${fieldId}-label`}>{field.label}</InputLabel>
            <Select
              id={fieldId}
              name={fieldId}
              labelId={`${fieldId}-label`}
              label={field.label}
              color="primary"
              value={field.state as string}
              onChange={
                field.onChangeCallback
                  ? (event) => field.onChangeCallback?.(event as unknown as React.ChangeEvent<HTMLInputElement>)
                  : undefined
              }
            >
              {field.values.map((value, index) => {
                return (
                  <MenuItem value={value.value} key={index}>
                    {value.label}
                  </MenuItem>
                );
              })}
            </Select>
            {field.helperText ? (
              <Typography variant="caption" color={field.error ? "error" : "text.secondary"} sx={{ mt: 0.5, ml: 1.75 }}>
                {field.helperText}
              </Typography>
            ) : null}
          </FormControl>
        );
      } else if (field.type === "RadioButton") {
        fieldElement = (
          <RadioGroup sx={styles.fieldItem && styles.radioButtonField} name={fieldId}>
            {field.values.map((value, index) => {
              return (
                <FormControlLabel
                  sx={styles.radioButtonItem}
                  value={value.value}
                  control={<Radio id={`${fieldId}-${index}`} />}
                  label={value.label}
                  key={`${value.label + index}`}
                  disabled={field.disabled}
                />
              );
            })}
          </RadioGroup>
        );
      } else if (field.type === "Text") {
        const textFieldProps = {
          sx: styles.fieldItem,
          error: field.error,
          helperText: field.helperText,
          key: index,
          id: fieldId,
          name: fieldId,
          label: field.label,
          color: "primary" as const,
          value: field.state,
          variant: "outlined" as const,
          onChange: field.onChangeCallback ? field.onChangeCallback : undefined,
          disabled: field.disabled,
        };
        fieldElement =
          field.inputType === "number" ? (
            <NumberField {...textFieldProps} inputProps={{ min: 0 }} />
          ) : (
            <TextField {...textFieldProps} type="text" />
          );
      } else if (field.type === "TextBig") {
        fieldElement = (
          <TextField
            sx={styles.fieldItem}
            key={index}
            id={fieldId}
            name={fieldId}
            label={field.label}
            color="primary"
            value={field.state}
            multiline
            rows={field.rows}
            variant="outlined"
            error={field.error}
            helperText={field.helperText}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            disabled={field.disabled}
          />
        );
      } else if (field.type === "Date") {
        fieldElement = (
          <DatePicker
            key={index}
            sx={styles.fieldItem}
            label={field.label}
            value={field.state as Dayjs}
            slotProps={{
              textField: {
                id: fieldId,
                name: fieldId,
                variant: "outlined",
                helperText: field.helperText,
                error: field.error,
              },
            }}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            disabled={field.disabled}
            views={field.views}
            format={field.format}
          />
        );
      } else if (field.type === "Time") {
        fieldElement = (
          <TimePicker
            key={index}
            sx={styles.fieldItem}
            label={field.label}
            value={field.state as Dayjs}
            slotProps={{
              textField: {
                id: fieldId,
                name: fieldId,
                variant: "outlined",
                helperText: field.helperText,
                error: field.error,
              },
            }}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            disabled={field.disabled}
          />
        );
      } else if (field.type === "DateTime") {
        fieldElement = (
          <DateTimePicker
            key={index}
            sx={styles.fieldItem}
            label={field.label}
            value={field.state as Dayjs}
            slotProps={{
              textField: {
                id: fieldId,
                name: fieldId,
                variant: "outlined",
                helperText: field.helperText,
                error: field.error,
              },
            }}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            disabled={field.disabled}
          />
        );
      } else if (field.type === "Autocomplete") {
        fieldElement = (
          <Autocomplete
            key={index}
            id={fieldId}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                {...params}
                id={fieldId}
                name={fieldId}
                label={field.label}
                helperText={field.helperText}
                error={field.error}
              />
            )}
            options={field.options}
            value={field.state}
            sx={styles.fieldItem}
            size="medium"
            multiple={field.multiple}
            disabled={field.disabled}
          />
        );
      } else if (field.type === "MembersAutocomplete") {
        fieldElement = (
          <Autocomplete
            key={index}
            id={fieldId}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            renderInput={(params) => (
              <TextField variant="outlined" {...params} id={fieldId} name={fieldId} label={field.label} />
            )}
            options={field.options}
            getOptionLabel={(option) => option.firstname + " " + option.lastname}
            isOptionEqualToValue={(option, value) => option.memberId === value.memberId}
            value={field.state as MembersFieldDto[]}
            sx={styles.fieldItem}
            size="medium"
            disabled={field.disabled}
            multiple
          />
        );
      } else if (field.type === "MemberAutocomplete") {
        fieldElement = (
          <Autocomplete
            key={index}
            id={fieldId}
            onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
            renderInput={(params) => (
              <TextField
                variant="outlined"
                {...params}
                id={fieldId}
                name={fieldId}
                label={field.label}
                helperText={field.helperText}
                error={field.error}
              />
            )}
            options={field.options}
            getOptionLabel={(option) => option.firstname + " " + option.lastname}
            isOptionEqualToValue={(option, value) => option.memberId === value.memberId}
            value={field.state as MembersFieldDto | null}
            sx={styles.fieldItem}
            size="medium"
            disabled={field.disabled}
          />
        );
      } else if (field.type === "Checkbox") {
        fieldElement = (
          <FormControlLabel
            sx={{ ...styles.fieldItem, ...styles.checkboxField }}
            control={
              <Checkbox
                key={index}
                id={fieldId}
                name={fieldId}
                color="primary"
                checked={field.state as boolean}
                onChange={field.onChangeCallback ? field.onChangeCallback : undefined}
                disabled={field.disabled}
              />
            }
            label={field.label}
          />
        );
      }

      let fieldContainer: React.JSX.Element;
      if (field.width === "half") {
        fieldContainer = (
          <Grid item xs={6} sx={styles.gridItem} key={index}>
            {fieldElement}
          </Grid>
        );
      } else if (field.width === "full") {
        fieldContainer = (
          <Grid item xs={12} sx={styles.gridItem} key={index}>
            {fieldElement}
          </Grid>
        );
      } else {
        fieldContainer = fieldElement;
      }

      return fieldContainer;
    });
  };

  return (
    <Box sx={styles.fieldSectionBox}>
      {props.title ? (
        <Typography variant="h5" sx={styles.fieldSectionTitle}>
          {props.title}
        </Typography>
      ) : null}
      <Grid container>{renderFields(props.fields)}</Grid>
    </Box>
  );
};

export default FieldSection;
export type { Field };
